// Catalog.ID Client — the modules, in the member's words
// ============================================================================
// desktop-setup §6.2/§6.3. Every other module already talks to the member in
// plain names: the Registry's copy says "forget Registry's registered
// identity", Files' says "forget Files' registered identity". Catalog.ID is
// where they are sent, and it used to answer in service ids (`cpr`, `efs`) or
// in the long label the module sends about itself ("Catalog Encrypted File
// Storage"). Three names for one thing, none of them the one the member was
// just given.
//
// The plain name comes FIRST and the service id second — never only one of
// them. The id is what a member types into a support question and what the
// module's own logs say, so dropping it would trade one blindness for another.
//
// The names are the plan's (`apps/docs/plans/desktop-setup.md` §2 "CPR
// (Registry)", §3 "EFS (Files)", §4 "cam (Camera)") and the module registry's
// codes (`MODULE_POLICY.md` §Registry). Anything not listed here is shown by
// the name it sent, or by its raw id: a module nobody has heard of is still
// evidence, and inventing a name for it would be worse than showing the truth.
//
// This is the ONLY map (desktop-setup §6.4). `pane-types/device-keys-core.ts`
// used to keep a second one for the key-use line, where `efs` read "File
// Storage"; it now reads `moduleName` like everything else, so the pane and
// the consent dialog cannot drift apart again.

const MODULE_NAMES: Readonly<Record<string, string>> = {
    cpr: 'Registry',
    efs: 'Files',
    cam: 'Camera',
    // `card` is the manifest code; `cards` is the folder, and both have been
    // seen on the wire.
    card: 'Cards',
    cards: 'Cards',
    chat: 'Chat',
    mail: 'Mail',
    xrpl: 'XRPL',
    bitcash: 'Bitcash',
    agent: 'Agent',
    work: 'Work',
    publisher: 'Publisher',
    desktop: 'Desktop',
    cid: 'Catalog.ID',
};

/** A packaged module registers under its own id; some carry the `cld-` prefix
 *  of the binary that ships them (`cld-xrpl`). Same module, same name. */
function normalize(serviceId: string): string {
    const id = serviceId.trim().toLowerCase();
    return id.startsWith('cld-') ? id.slice('cld-'.length) : id;
}

/**
 * The module's name as the member reads it.
 *
 * `sentLabel` is what the module said about itself in its envelope
 * (`requesterLabel`). It is used ONLY for a module this map does not know:
 * for a known one the member's word wins, because that is the word the
 * module's own guides and refusals use.
 */
export function moduleName(serviceId: string, sentLabel?: string): string {
    return MODULE_NAMES[normalize(serviceId)]
        ?? (sentLabel && sentLabel.trim() ? sentLabel.trim() : serviceId);
}

/**
 * The name first, the service id second — "Files (efs)". When the two would
 * read the same, the id alone: "newmodule (newmodule)" is noise.
 */
export function moduleNameWithId(serviceId: string, sentLabel?: string): string {
    const name = moduleName(serviceId, sentLabel);
    return name === serviceId ? serviceId : `${name} (${serviceId})`;
}
